/**
 * Durable client-side queue for the official "wait for the next run"
 * follow-up behavior.
 *
 * Integration order:
 * 1. Call route() when the composer is submitted. The default behavior is
 *    steer-current, which never writes to this queue.
 * 2. Feed authoritative turn/completed events to noteTurnCompleted().
 * 3. Call claimReady() and submit the returned payload with turn/start and the
 *    same clientMessageId.
 * 4. Call acknowledgeRpcAccepted() only after turn/start is explicitly
 *    accepted and returns its new turn ID. A definitive rejection may call
 *    releaseAfterRpcRejection(); an unknown outcome must remain dispatching.
 * 5. After a restart, inspect inFlight() before claiming new work. Retrying an
 *    in-flight request is safe only when the gateway honors clientMessageId as
 *    an idempotency key.
 */

export const WORK_FOLLOW_UP_QUEUE_SCHEMA_VERSION = 1 as const;
export const DEFAULT_WORK_FOLLOW_UP_BEHAVIOR = "steer-current" as const;

const STORAGE_PREFIX = "bianhua-work-follow-up-queue:v1";
const MAX_QUEUE_ENTRIES = 256;
const MAX_ACCEPTED_RECEIPTS = 4_096;
const MAX_EARLY_COMPLETIONS = 32;
const MAX_PAYLOAD_CHARACTERS = 1_048_576;

export type WorkFollowUpBehavior = "steer-current" | "wait-for-next";
export type WorkFollowUpJsonValue =
  | null
  | boolean
  | number
  | string
  | WorkFollowUpJsonValue[]
  | { [key: string]: WorkFollowUpJsonValue };

export interface WorkFollowUpStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type WorkFollowUpQueueErrorCode =
  | "invalid_input"
  | "idempotency_conflict"
  | "invalid_transition"
  | "invalid_journal"
  | "persistence_failed"
  | "concurrent_modification"
  | "queue_full";

export class WorkFollowUpQueueError extends Error {
  readonly code: WorkFollowUpQueueErrorCode;

  constructor(code: WorkFollowUpQueueErrorCode, message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "WorkFollowUpQueueError";
    this.code = code;
  }
}

export type WorkFollowUpQueueOptions = {
  principalId: string;
  projectId: string;
  threadId: string;
  storage?: WorkFollowUpStorage;
  now?: () => string;
};

export type RouteWorkFollowUpInput<TPayload> = {
  clientMessageId: string;
  activeTurnId: string | null;
  payload: TPayload;
};

export type EnqueueWorkFollowUpInput<TPayload> = {
  clientMessageId: string;
  activeTurnId: string;
  payload: TPayload;
};

export type WorkFollowUpEntryState = "queued" | "waiting-for-turn" | "ready" | "dispatching";

export type WorkFollowUpQueueEntry<TPayload> = {
  clientMessageId: string;
  payload: TPayload;
  createdAt: string;
  state: WorkFollowUpEntryState;
  blockedByTurnId: string | null;
};

export type WorkFollowUpAcceptedReceipt = {
  clientMessageId: string;
  acceptedTurnId: string;
  acceptedAt: string;
};

export type WorkFollowUpEnqueueResult<TPayload> =
  | { kind: "queued"; entry: WorkFollowUpQueueEntry<TPayload>; duplicate: boolean }
  | { kind: "already-accepted"; receipt: WorkFollowUpAcceptedReceipt };

export type WorkFollowUpRouteDecision<TPayload> =
  | { action: "start-now"; clientMessageId: string; payload: TPayload }
  | {
      action: "steer-current";
      clientMessageId: string;
      expectedTurnId: string;
      payload: TPayload;
    }
  | { action: "queued"; entry: WorkFollowUpQueueEntry<TPayload>; duplicate: boolean }
  | { action: "already-accepted"; receipt: WorkFollowUpAcceptedReceipt };

export type WorkFollowUpQueueStatus = "empty" | "waiting-for-turn" | "ready" | "dispatching";

export type WorkFollowUpQueueSnapshot<TPayload> = {
  principalId: string;
  projectId: string;
  threadId: string;
  storageKey: string;
  revision: number;
  status: WorkFollowUpQueueStatus;
  size: number;
  acceptedCount: number;
  blockingTurnId: string | null;
  blockingTurnCompleted: boolean;
  canClaimHead: boolean;
  hasInFlightRequest: boolean;
  recoveredInFlight: boolean;
  head: WorkFollowUpQueueEntry<TPayload> | null;
  entries: WorkFollowUpQueueEntry<TPayload>[];
  lastAccepted: WorkFollowUpAcceptedReceipt | null;
};

export type WorkTurnCompletionResult<TPayload> = {
  outcome: "ignored" | "unlocked" | "already-unlocked" | "recorded-before-rpc-acceptance";
  snapshot: WorkFollowUpQueueSnapshot<TPayload>;
};

type StoredEntry = {
  clientMessageId: string;
  payload: WorkFollowUpJsonValue;
  payloadFingerprint: string;
  createdAt: string;
  state: "queued" | "dispatching";
};

type StoredReceipt = {
  clientMessageId: string;
  payloadFingerprint: string;
  acceptedTurnId: string;
  acceptedAt: string;
};

type TurnGate = { turnId: string; completed: boolean };

type QueueJournal = {
  schemaVersion: typeof WORK_FOLLOW_UP_QUEUE_SCHEMA_VERSION;
  principalId: string;
  projectId: string;
  threadId: string;
  revision: number;
  gate: TurnGate | null;
  entries: StoredEntry[];
  acceptedReceipts: StoredReceipt[];
  earlyCompletedTurnIds: string[];
};

type UnknownRecord = Record<string, unknown>;
type SnapshotListener<TPayload> = (snapshot: WorkFollowUpQueueSnapshot<TPayload>) => void;

function isRecord(value: unknown): value is UnknownRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: UnknownRecord, required: readonly string[]): boolean {
  const allowed = new Set(required);
  return required.every((key) => Object.hasOwn(value, key))
    && Object.keys(value).every((key) => allowed.has(key));
}

function validIdentifier(value: unknown): value is string {
  return typeof value === "string"
    && value.length > 0
    && value.length <= 256
    && value.trim() === value
    && !/[\u0000-\u001f\u007f]/.test(value);
}

function validTimestamp(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function normalizeJson(value: unknown, ancestors: Set<object>): WorkFollowUpJsonValue {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new WorkFollowUpQueueError("invalid_input", "Follow-up payload contains a non-finite number");
    return value;
  }
  if (typeof value !== "object") {
    throw new WorkFollowUpQueueError("invalid_input", "Follow-up payload must contain JSON-compatible values only");
  }
  if (ancestors.has(value)) throw new WorkFollowUpQueueError("invalid_input", "Follow-up payload contains a cycle");
  ancestors.add(value);
  try {
    if (Array.isArray(value)) return value.map((item) => normalizeJson(item, ancestors));
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      throw new WorkFollowUpQueueError("invalid_input", "Follow-up payload contains a non-plain object");
    }
    const normalized: { [key: string]: WorkFollowUpJsonValue } = {};
    for (const key of Object.keys(value as UnknownRecord).sort()) {
      normalized[key] = normalizeJson((value as UnknownRecord)[key], ancestors);
    }
    return normalized;
  } finally {
    ancestors.delete(value);
  }
}

function normalizePayload<TPayload>(value: TPayload): {
  payload: WorkFollowUpJsonValue;
  fingerprint: string;
} {
  let payload: WorkFollowUpJsonValue;
  try {
    payload = normalizeJson(value, new Set());
  } catch (error) {
    if (error instanceof WorkFollowUpQueueError) throw error;
    throw new WorkFollowUpQueueError("invalid_input", "Follow-up payload could not be read", { cause: error });
  }
  const fingerprint = JSON.stringify(payload);
  if (fingerprint.length > MAX_PAYLOAD_CHARACTERS) {
    throw new WorkFollowUpQueueError("invalid_input", "Follow-up payload is too large for the durable queue");
  }
  return { payload, fingerprint };
}

function clonePayload<TPayload>(payload: WorkFollowUpJsonValue): TPayload {
  return JSON.parse(JSON.stringify(payload)) as TPayload;
}

function cloneReceipt(receipt: StoredReceipt): WorkFollowUpAcceptedReceipt {
  return {
    clientMessageId: receipt.clientMessageId,
    acceptedTurnId: receipt.acceptedTurnId,
    acceptedAt: receipt.acceptedAt,
  };
}

function resolveStorage(storage: WorkFollowUpStorage | undefined): WorkFollowUpStorage {
  if (storage) return storage;
  const candidate = (globalThis as typeof globalThis & { localStorage?: WorkFollowUpStorage }).localStorage;
  if (!candidate) {
    throw new WorkFollowUpQueueError("persistence_failed", "Persistent storage is unavailable");
  }
  return candidate;
}

export function workFollowUpQueueStorageKey(principalId: string, projectId: string, threadId: string): string {
  if (!validIdentifier(principalId) || !validIdentifier(projectId) || !validIdentifier(threadId)) {
    throw new WorkFollowUpQueueError("invalid_input", "Follow-up queue scope contains a malformed identifier");
  }
  return `${STORAGE_PREFIX}:${encodeURIComponent(principalId)}:${encodeURIComponent(projectId)}:${encodeURIComponent(threadId)}`;
}

function emptyJournal(principalId: string, projectId: string, threadId: string): QueueJournal {
  return {
    schemaVersion: WORK_FOLLOW_UP_QUEUE_SCHEMA_VERSION,
    principalId,
    projectId,
    threadId,
    revision: 0,
    gate: null,
    entries: [],
    acceptedReceipts: [],
    earlyCompletedTurnIds: [],
  };
}

function parseJournal(raw: string, principalId: string, projectId: string, threadId: string): QueueJournal {
  let value: unknown;
  try {
    value = JSON.parse(raw) as unknown;
  } catch (error) {
    throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue journal is not valid JSON", { cause: error });
  }
  const keys = [
    "schemaVersion", "principalId", "projectId", "threadId", "revision", "gate", "entries",
    "acceptedReceipts", "earlyCompletedTurnIds",
  ];
  if (!isRecord(value)
    || !exactKeys(value, keys)
    || value.schemaVersion !== WORK_FOLLOW_UP_QUEUE_SCHEMA_VERSION
    || value.principalId !== principalId
    || value.projectId !== projectId
    || value.threadId !== threadId
    || !Number.isSafeInteger(value.revision)
    || (value.revision as number) < 0
    || !Array.isArray(value.entries)
    || value.entries.length > MAX_QUEUE_ENTRIES
    || !Array.isArray(value.acceptedReceipts)
    || value.acceptedReceipts.length > MAX_ACCEPTED_RECEIPTS
    || !Array.isArray(value.earlyCompletedTurnIds)
    || value.earlyCompletedTurnIds.length > MAX_EARLY_COMPLETIONS) {
    throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue journal scope or structure is invalid");
  }

  if (value.gate !== null && (!isRecord(value.gate)
    || !exactKeys(value.gate, ["turnId", "completed"])
    || !validIdentifier(value.gate.turnId)
    || typeof value.gate.completed !== "boolean")) {
    throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue turn gate is invalid");
  }

  const seen = new Set<string>();
  for (let index = 0; index < value.entries.length; index += 1) {
    const entry = value.entries[index];
    if (!isRecord(entry)
      || !exactKeys(entry, ["clientMessageId", "payload", "payloadFingerprint", "createdAt", "state"])
      || !validIdentifier(entry.clientMessageId)
      || !validTimestamp(entry.createdAt)
      || (entry.state !== "queued" && entry.state !== "dispatching")
      || (entry.state === "dispatching" && index !== 0)
      || typeof entry.payloadFingerprint !== "string"
      || entry.payloadFingerprint.length > MAX_PAYLOAD_CHARACTERS) {
      throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue contains a malformed entry");
    }
    let normalized: ReturnType<typeof normalizePayload>;
    try {
      normalized = normalizePayload(entry.payload);
    } catch (error) {
      throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue contains an invalid payload", { cause: error });
    }
    if (normalized.fingerprint !== entry.payloadFingerprint || seen.has(entry.clientMessageId)) {
      throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue contains a duplicate or altered entry");
    }
    seen.add(entry.clientMessageId);
  }

  for (const receipt of value.acceptedReceipts) {
    if (!isRecord(receipt)
      || !exactKeys(receipt, ["clientMessageId", "payloadFingerprint", "acceptedTurnId", "acceptedAt"])
      || !validIdentifier(receipt.clientMessageId)
      || !validIdentifier(receipt.acceptedTurnId)
      || !validTimestamp(receipt.acceptedAt)
      || typeof receipt.payloadFingerprint !== "string"
      || receipt.payloadFingerprint.length > MAX_PAYLOAD_CHARACTERS
      || seen.has(receipt.clientMessageId)) {
      throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue contains a malformed receipt");
    }
    seen.add(receipt.clientMessageId);
  }

  const early = value.earlyCompletedTurnIds;
  if (!early.every(validIdentifier) || new Set(early).size !== early.length) {
    throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue contains malformed early completion records");
  }
  const entries = value.entries as StoredEntry[];
  const gate = value.gate as TurnGate | null;
  if ((entries.length === 0) !== (gate === null)
    || (entries[0]?.state === "dispatching" && !gate?.completed)
    || (early.length > 0 && entries[0]?.state !== "dispatching")) {
    throw new WorkFollowUpQueueError("invalid_journal", "Follow-up queue journal has an impossible state");
  }
  return value as unknown as QueueJournal;
}

function cloneJournal(journal: QueueJournal): QueueJournal {
  return {
    schemaVersion: WORK_FOLLOW_UP_QUEUE_SCHEMA_VERSION,
    principalId: journal.principalId,
    projectId: journal.projectId,
    threadId: journal.threadId,
    revision: journal.revision,
    gate: journal.gate ? { ...journal.gate } : null,
    entries: journal.entries.map((entry) => ({
      ...entry,
      payload: clonePayload<WorkFollowUpJsonValue>(entry.payload),
    })),
    acceptedReceipts: journal.acceptedReceipts.map((receipt) => ({ ...receipt })),
    earlyCompletedTurnIds: [...journal.earlyCompletedTurnIds],
  };
}

export class WorkFollowUpQueue<TPayload = WorkFollowUpJsonValue> {
  readonly principalId: string;
  readonly projectId: string;
  readonly threadId: string;
  readonly storageKey: string;

  private readonly storage: WorkFollowUpStorage;
  private readonly now: () => string;
  private readonly listeners = new Set<SnapshotListener<TPayload>>();
  private journal: QueueJournal;
  private loadedRaw: string | null;
  private recoveredInFlightId: string | null;

  constructor(options: WorkFollowUpQueueOptions) {
    this.storageKey = workFollowUpQueueStorageKey(options.principalId, options.projectId, options.threadId);
    this.principalId = options.principalId;
    this.projectId = options.projectId;
    this.threadId = options.threadId;
    this.storage = resolveStorage(options.storage);
    this.now = options.now ?? (() => new Date().toISOString());
    const recovered = this.readJournal();
    this.journal = recovered.journal;
    this.loadedRaw = recovered.raw;
    this.recoveredInFlightId = recovered.raw && recovered.journal.entries[0]?.state === "dispatching"
      ? recovered.journal.entries[0].clientMessageId
      : null;
  }

  /** Default steer-current routes directly and never mutates the durable queue. */
  route(
    input: RouteWorkFollowUpInput<TPayload>,
    behavior: WorkFollowUpBehavior = DEFAULT_WORK_FOLLOW_UP_BEHAVIOR,
  ): WorkFollowUpRouteDecision<TPayload> {
    if (behavior !== "steer-current" && behavior !== "wait-for-next") {
      throw new WorkFollowUpQueueError("invalid_input", "Unknown follow-up behavior");
    }
    const normalized = this.normalizedInput(input.clientMessageId, input.payload);
    const known = this.findKnown(normalized.clientMessageId, normalized.fingerprint);
    if (known) return known.kind === "entry"
      ? { action: "queued", entry: this.entryView(known.entry, known.index), duplicate: true }
      : { action: "already-accepted", receipt: cloneReceipt(known.receipt) };

    if (input.activeTurnId === null) {
      if (this.journal.entries.length > 0) {
        throw new WorkFollowUpQueueError(
          "invalid_transition",
          "A durable follow-up is pending; dispatch the queued head before starting newer work",
        );
      }
      return {
        action: "start-now",
        clientMessageId: normalized.clientMessageId,
        payload: clonePayload<TPayload>(normalized.payload),
      };
    }
    if (!validIdentifier(input.activeTurnId)) {
      throw new WorkFollowUpQueueError("invalid_input", "Active turn ID is malformed");
    }
    if (behavior === "steer-current") {
      return {
        action: "steer-current",
        clientMessageId: normalized.clientMessageId,
        expectedTurnId: input.activeTurnId,
        payload: clonePayload<TPayload>(normalized.payload),
      };
    }
    const queued = this.enqueueNormalized(normalized, input.activeTurnId);
    return queued.kind === "queued"
      ? { action: "queued", entry: queued.entry, duplicate: queued.duplicate }
      : { action: "already-accepted", receipt: queued.receipt };
  }

  enqueueWaitForNext(input: EnqueueWorkFollowUpInput<TPayload>): WorkFollowUpEnqueueResult<TPayload> {
    if (!validIdentifier(input.activeTurnId)) {
      throw new WorkFollowUpQueueError("invalid_input", "Active turn ID is malformed");
    }
    return this.enqueueNormalized(this.normalizedInput(input.clientMessageId, input.payload), input.activeTurnId);
  }

  /** Records the authoritative terminal event that unlocks the FIFO head. */
  noteTurnCompleted(turnId: string): WorkTurnCompletionResult<TPayload> {
    if (!validIdentifier(turnId)) throw new WorkFollowUpQueueError("invalid_input", "Completed turn ID is malformed");
    const gate = this.journal.gate;
    if (!gate) return { outcome: "ignored", snapshot: this.snapshot() };
    if (gate.turnId === turnId) {
      if (gate.completed) return { outcome: "already-unlocked", snapshot: this.snapshot() };
      const next = cloneJournal(this.journal);
      next.gate!.completed = true;
      next.revision += 1;
      this.commit(next);
      return { outcome: "unlocked", snapshot: this.snapshot() };
    }
    if (this.journal.entries[0]?.state !== "dispatching") {
      return { outcome: "ignored", snapshot: this.snapshot() };
    }
    if (this.journal.earlyCompletedTurnIds.includes(turnId)) {
      return { outcome: "recorded-before-rpc-acceptance", snapshot: this.snapshot() };
    }
    if (this.journal.earlyCompletedTurnIds.length >= MAX_EARLY_COMPLETIONS) {
      throw new WorkFollowUpQueueError("queue_full", "Too many unmatched completion events require reconciliation");
    }
    const next = cloneJournal(this.journal);
    next.earlyCompletedTurnIds.push(turnId);
    next.revision += 1;
    this.commit(next);
    return { outcome: "recorded-before-rpc-acceptance", snapshot: this.snapshot() };
  }

  /** Durably marks the ready head in flight without removing it. */
  claimReady(): WorkFollowUpQueueEntry<TPayload> | null {
    const head = this.journal.entries[0];
    if (!head || head.state === "dispatching" || !this.journal.gate?.completed) return null;
    const next = cloneJournal(this.journal);
    next.entries[0]!.state = "dispatching";
    next.revision += 1;
    this.commit(next);
    this.recoveredInFlightId = null;
    return this.entryView(this.journal.entries[0]!, 0);
  }

  /** Returns a crash-recovered/unknown-outcome request without changing it. */
  inFlight(): WorkFollowUpQueueEntry<TPayload> | null {
    const head = this.journal.entries[0];
    return head?.state === "dispatching" ? this.entryView(head, 0) : null;
  }

  /**
   * Deletes the FIFO head only after explicit RPC acceptance. The returned turn
   * becomes the blocker for the next queued message.
   */
  acknowledgeRpcAccepted(clientMessageId: string, acceptedTurnId: string): WorkFollowUpAcceptedReceipt {
    if (!validIdentifier(clientMessageId) || !validIdentifier(acceptedTurnId)) {
      throw new WorkFollowUpQueueError("invalid_input", "Accepted follow-up identifiers are malformed");
    }
    const existing = this.journal.acceptedReceipts.find((receipt) => receipt.clientMessageId === clientMessageId);
    if (existing) {
      if (existing.acceptedTurnId !== acceptedTurnId) {
        throw new WorkFollowUpQueueError("idempotency_conflict", "Accepted follow-up was associated with a different turn");
      }
      return cloneReceipt(existing);
    }
    const head = this.journal.entries[0];
    if (!head || head.clientMessageId !== clientMessageId || head.state !== "dispatching") {
      throw new WorkFollowUpQueueError("invalid_transition", "Only the in-flight FIFO head can be acknowledged");
    }
    if (acceptedTurnId === this.journal.gate?.turnId) {
      throw new WorkFollowUpQueueError("invalid_transition", "A newly accepted follow-up must have a new turn ID");
    }
    if (this.journal.acceptedReceipts.length >= MAX_ACCEPTED_RECEIPTS) {
      throw new WorkFollowUpQueueError("queue_full", "Accepted follow-up receipt journal is full");
    }
    const acceptedAt = this.checkedNow(head.createdAt);
    const receipt: StoredReceipt = {
      clientMessageId,
      payloadFingerprint: head.payloadFingerprint,
      acceptedTurnId,
      acceptedAt,
    };
    const next = cloneJournal(this.journal);
    next.entries.shift();
    next.acceptedReceipts.push(receipt);
    const completionOvertookRpc = next.earlyCompletedTurnIds.includes(acceptedTurnId);
    next.earlyCompletedTurnIds = [];
    next.gate = next.entries.length > 0 ? { turnId: acceptedTurnId, completed: completionOvertookRpc } : null;
    next.revision += 1;
    this.commit(next);
    this.recoveredInFlightId = null;
    return cloneReceipt(receipt);
  }

  /** A definitive rejection returns the same head to ready; unknown outcomes must not call this. */
  releaseAfterRpcRejection(clientMessageId: string): WorkFollowUpQueueEntry<TPayload> {
    if (!validIdentifier(clientMessageId)) {
      throw new WorkFollowUpQueueError("invalid_input", "Rejected follow-up ID is malformed");
    }
    const head = this.journal.entries[0];
    if (!head || head.clientMessageId !== clientMessageId || head.state !== "dispatching") {
      throw new WorkFollowUpQueueError("invalid_transition", "Only the in-flight FIFO head can be released");
    }
    const next = cloneJournal(this.journal);
    next.entries[0]!.state = "queued";
    next.earlyCompletedTurnIds = [];
    next.revision += 1;
    this.commit(next);
    this.recoveredInFlightId = null;
    return this.entryView(this.journal.entries[0]!, 0);
  }

  /**
   * Durably discards a definitively rejected in-flight head so its payload can
   * be restored to the composer. This must never be used for an unknown RPC
   * outcome. No new turn started, so the next FIFO item remains unlocked by
   * the same already-completed gate.
   */
  discardAfterRpcRejection(clientMessageId: string): WorkFollowUpQueueEntry<TPayload> {
    if (!validIdentifier(clientMessageId)) {
      throw new WorkFollowUpQueueError("invalid_input", "Discarded follow-up ID is malformed");
    }
    const head = this.journal.entries[0];
    if (!head || head.clientMessageId !== clientMessageId || head.state !== "dispatching") {
      throw new WorkFollowUpQueueError(
        "invalid_transition",
        "Only the definitively rejected in-flight FIFO head can be discarded",
      );
    }
    const discarded = this.entryView(head, 0);
    const next = cloneJournal(this.journal);
    next.entries.shift();
    next.earlyCompletedTurnIds = [];
    if (next.entries.length === 0) next.gate = null;
    next.revision += 1;
    this.commit(next);
    this.recoveredInFlightId = null;
    return discarded;
  }

  snapshot(): WorkFollowUpQueueSnapshot<TPayload> {
    const entries = this.journal.entries.map((entry, index) => this.entryView(entry, index));
    const head = entries[0] ?? null;
    const status: WorkFollowUpQueueStatus = !head
      ? "empty"
      : head.state === "dispatching"
        ? "dispatching"
        : head.state === "ready" ? "ready" : "waiting-for-turn";
    const lastAccepted = this.journal.acceptedReceipts.at(-1);
    return {
      principalId: this.principalId,
      projectId: this.projectId,
      threadId: this.threadId,
      storageKey: this.storageKey,
      revision: this.journal.revision,
      status,
      size: entries.length,
      acceptedCount: this.journal.acceptedReceipts.length,
      blockingTurnId: this.journal.gate?.turnId ?? null,
      blockingTurnCompleted: this.journal.gate?.completed ?? false,
      canClaimHead: status === "ready",
      hasInFlightRequest: status === "dispatching",
      recoveredInFlight: Boolean(
        head
        && head.state === "dispatching"
        && head.clientMessageId === this.recoveredInFlightId,
      ),
      head,
      entries,
      lastAccepted: lastAccepted ? cloneReceipt(lastAccepted) : null,
    };
  }

  subscribe(listener: SnapshotListener<TPayload>, emitCurrent = true): () => void {
    this.listeners.add(listener);
    if (emitCurrent) listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  /** Re-reads durable state after another tab/process changed it or a write outcome was uncertain. */
  recover(): WorkFollowUpQueueSnapshot<TPayload> {
    const recovered = this.readJournal();
    this.journal = recovered.journal;
    this.loadedRaw = recovered.raw;
    this.recoveredInFlightId = recovered.journal.entries[0]?.state === "dispatching"
      ? recovered.journal.entries[0].clientMessageId
      : null;
    this.emit();
    return this.snapshot();
  }

  private enqueueNormalized(
    normalized: { clientMessageId: string; payload: WorkFollowUpJsonValue; fingerprint: string },
    activeTurnId: string,
  ): WorkFollowUpEnqueueResult<TPayload> {
    const known = this.findKnown(normalized.clientMessageId, normalized.fingerprint);
    if (known) return known.kind === "entry"
      ? { kind: "queued", entry: this.entryView(known.entry, known.index), duplicate: true }
      : { kind: "already-accepted", receipt: cloneReceipt(known.receipt) };
    if (this.journal.entries.length >= MAX_QUEUE_ENTRIES) {
      throw new WorkFollowUpQueueError("queue_full", "Follow-up queue is full");
    }
    if (this.journal.entries.length > 0
      && !this.journal.gate?.completed
      && this.journal.gate?.turnId !== activeTurnId) {
      throw new WorkFollowUpQueueError("invalid_transition", "Queued follow-ups are waiting for a different active turn");
    }
    const createdAt = this.checkedNow();
    const next = cloneJournal(this.journal);
    if (next.entries.length === 0) next.gate = { turnId: activeTurnId, completed: false };
    next.entries.push({
      clientMessageId: normalized.clientMessageId,
      payload: normalized.payload,
      payloadFingerprint: normalized.fingerprint,
      createdAt,
      state: "queued",
    });
    next.revision += 1;
    this.commit(next);
    return {
      kind: "queued",
      entry: this.entryView(this.journal.entries.at(-1)!, this.journal.entries.length - 1),
      duplicate: false,
    };
  }

  private normalizedInput(clientMessageId: string, payload: TPayload): {
    clientMessageId: string;
    payload: WorkFollowUpJsonValue;
    fingerprint: string;
  } {
    if (!validIdentifier(clientMessageId)) {
      throw new WorkFollowUpQueueError("invalid_input", "Client message ID is malformed");
    }
    return { clientMessageId, ...normalizePayload(payload) };
  }

  private findKnown(clientMessageId: string, fingerprint: string):
    | { kind: "entry"; entry: StoredEntry; index: number }
    | { kind: "receipt"; receipt: StoredReceipt }
    | null {
    const index = this.journal.entries.findIndex((entry) => entry.clientMessageId === clientMessageId);
    if (index >= 0) {
      const entry = this.journal.entries[index]!;
      if (entry.payloadFingerprint !== fingerprint) {
        throw new WorkFollowUpQueueError("idempotency_conflict", "Client message ID was reused with a different payload");
      }
      return { kind: "entry", entry, index };
    }
    const receipt = this.journal.acceptedReceipts.find((candidate) => candidate.clientMessageId === clientMessageId);
    if (!receipt) return null;
    if (receipt.payloadFingerprint !== fingerprint) {
      throw new WorkFollowUpQueueError("idempotency_conflict", "Accepted client message ID was reused with a different payload");
    }
    return { kind: "receipt", receipt };
  }

  private entryView(entry: StoredEntry, index: number): WorkFollowUpQueueEntry<TPayload> {
    let state: WorkFollowUpEntryState = "queued";
    let blockedByTurnId: string | null = null;
    if (index === 0) {
      if (entry.state === "dispatching") state = "dispatching";
      else if (this.journal.gate?.completed) state = "ready";
      else {
        state = "waiting-for-turn";
        blockedByTurnId = this.journal.gate?.turnId ?? null;
      }
    }
    return {
      clientMessageId: entry.clientMessageId,
      payload: clonePayload<TPayload>(entry.payload),
      createdAt: entry.createdAt,
      state,
      blockedByTurnId,
    };
  }

  private checkedNow(notBefore?: string): string {
    const value = this.now();
    if (!validTimestamp(value) || (notBefore !== undefined && Date.parse(value) < Date.parse(notBefore))) {
      throw new WorkFollowUpQueueError("invalid_input", "Clock returned an invalid timestamp");
    }
    return value;
  }

  private readJournal(): { journal: QueueJournal; raw: string | null } {
    let raw: string | null;
    try {
      raw = this.storage.getItem(this.storageKey);
    } catch (error) {
      throw new WorkFollowUpQueueError("persistence_failed", "Follow-up queue journal cannot be read", { cause: error });
    }
    return {
      journal: raw === null ? emptyJournal(this.principalId, this.projectId, this.threadId) : parseJournal(
        raw,
        this.principalId,
        this.projectId,
        this.threadId,
      ),
      raw,
    };
  }

  private commit(next: QueueJournal): void {
    const serialized = JSON.stringify(next);
    try {
      const current = this.storage.getItem(this.storageKey);
      if (current !== this.loadedRaw) {
        throw new WorkFollowUpQueueError(
          "concurrent_modification",
          "Follow-up queue changed in another client; recover it before retrying",
        );
      }
      this.storage.setItem(this.storageKey, serialized);
      if (this.storage.getItem(this.storageKey) !== serialized) {
        throw new Error("follow-up queue write verification failed");
      }
    } catch (error) {
      if (error instanceof WorkFollowUpQueueError) throw error;
      throw new WorkFollowUpQueueError(
        "persistence_failed",
        "Follow-up queue journal could not be durably written",
        { cause: error },
      );
    }
    this.journal = next;
    this.loadedRaw = serialized;
    this.emit();
  }

  private emit(): void {
    const snapshot = this.snapshot();
    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      } catch {
        // Persistence has already committed; observer failures cannot roll it back.
      }
    }
  }
}
